import React, { useEffect, useState } from "react";
import { Building2, Plus, Pencil } from "lucide-react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { Card, Loading, PageHeader, Button, Modal, Badge } from "@/components/common";

const TYPES = [["hub", "Toko Utama / Hub"], ["store", "Cabang / Toko"], ["consignment", "Titip Pasar"], ["mobile", "Electric Bike / Mobile"]];
const TYPE_LABEL = Object.fromEntries(TYPES);

export default function Branches() {
  const { can, isOwner, refreshBranches } = useAuth();
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const canManage = can("settings", "create") || isOwner;

  const load = async () => {
    setLoading(true);
    try { setList(await api.get("/branches", { include_inactive: true })); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  return (
    <>
      <PageHeader title="Cabang / Unit Bisnis" subtitle={`${list.length} unit`} icon={Building2}
        actions={canManage && <Button onClick={() => setEditing({})} data-testid="add-branch-btn"><Plus size={16} /> Cabang Baru</Button>} />

      {loading ? <Loading /> : (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
          {list.map((b) => (
            <Card key={b.id} className="p-4" data-testid={`branch-${b.code}`}>
              <div className="flex items-start justify-between">
                <div>
                  <div className="font-semibold text-slate-800">{b.name}</div>
                  <div className="text-xs text-slate-400">Kode {b.code}</div>
                </div>
                {canManage && <button onClick={() => setEditing(b)} className="w-8 h-8 rounded-lg hover:bg-slate-100 flex items-center justify-center text-slate-500"><Pencil size={15} /></button>}
              </div>
              <div className="flex flex-wrap gap-2 mt-3">
                <Badge className="bg-primary/10 text-primary">{TYPE_LABEL[b.type] || b.type}</Badge>
                {b.is_active === false ? <Badge className="bg-rose-100 text-rose-700">Nonaktif</Badge> : <Badge className="bg-emerald-100 text-emerald-700">Aktif</Badge>}
              </div>
              {b.address && <div className="text-xs text-slate-400 mt-2">{b.address}</div>}
              <div className="text-xs text-slate-400 mt-1">Shift mulai {b.shift_start || "-"} · radius {b.radius_m || 0}m</div>
            </Card>
          ))}
        </div>
      )}

      {editing && <BranchModal branch={editing} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); load(); refreshBranches(); }} />}
    </>
  );
}

function BranchModal({ branch, onClose, onSaved }) {
  const isNew = !branch.id;
  const [form, setForm] = useState({
    name: branch.name || "", code: branch.code || "", type: branch.type || "store", address: branch.address || "",
    phone: branch.phone || "", shift_start: branch.shift_start || "09:00", radius_m: branch.radius_m || 150,
    lat: branch.lat ?? "", lng: branch.lng ?? "", is_active: branch.is_active ?? true,
  });
  const [busy, setBusy] = useState(false);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const save = async () => {
    if (!form.name.trim() || !form.code.trim()) return toast.error("Nama & kode wajib diisi");
    setBusy(true);
    try {
      const body = { ...form, radius_m: Number(form.radius_m), lat: form.lat === "" ? null : Number(form.lat), lng: form.lng === "" ? null : Number(form.lng) };
      if (isNew) await api.post("/branches", body);
      else await api.patch(`/branches/${branch.id}`, body);
      toast.success(isNew ? "Cabang dibuat" : "Cabang diperbarui");
      onSaved();
    } catch (e) { toast.error(e.message); } finally { setBusy(false); }
  };

  return (
    <Modal open onClose={onClose} title={isNew ? "Cabang Baru" : "Edit Cabang"} maxWidth="max-w-md">
      <div className="space-y-3">
        <F label="Nama"><input value={form.name} onChange={(e) => set("name", e.target.value)} className="binp" data-testid="bf-name" /></F>
        <div className="grid grid-cols-2 gap-3">
          <F label="Kode"><input value={form.code} onChange={(e) => set("code", e.target.value)} className="binp" disabled={!isNew} /></F>
          <F label="Tipe"><select value={form.type} onChange={(e) => set("type", e.target.value)} className="binp">{TYPES.map(([v, l]) => <option key={v} value={v}>{l}</option>)}</select></F>
        </div>
        <F label="Alamat"><textarea value={form.address} onChange={(e) => set("address", e.target.value)} rows={2} className="binp" /></F>
        <div className="grid grid-cols-2 gap-3">
          <F label="Telepon"><input value={form.phone} onChange={(e) => set("phone", e.target.value)} className="binp" /></F>
          <F label="Shift Mulai"><input value={form.shift_start} onChange={(e) => set("shift_start", e.target.value)} placeholder="09:00" className="binp" /></F>
        </div>
        <div className="grid grid-cols-3 gap-3">
          <F label="Latitude"><input value={form.lat} onChange={(e) => set("lat", e.target.value)} className="binp" /></F>
          <F label="Longitude"><input value={form.lng} onChange={(e) => set("lng", e.target.value)} className="binp" /></F>
          <F label="Radius (m)"><input type="number" value={form.radius_m} onChange={(e) => set("radius_m", e.target.value)} className="binp" /></F>
        </div>
        {!isNew && (
          <label className="flex items-center gap-2 text-sm text-slate-600"><input type="checkbox" checked={form.is_active} onChange={(e) => set("is_active", e.target.checked)} /> Aktif</label>
        )}
        <Button className="w-full" onClick={save} disabled={busy} data-testid="bf-save">{busy ? "Menyimpan..." : "Simpan"}</Button>
      </div>
      <style>{`.binp{width:100%;height:2.5rem;padding:0 .75rem;border:1px solid #cbd5e1;border-radius:.75rem;font-size:.875rem;outline:none}.binp:focus{border-color:hsl(var(--primary))}textarea.binp{height:auto;padding:.5rem .75rem}`}</style>
    </Modal>
  );
}

const F = ({ label, children }) => (
  <label className="block"><span className="text-sm font-medium text-slate-600 mb-1 block">{label}</span>{children}</label>
);
